/**
 * <announcement-bar>: rotating announcements with optional scheduling.
 *
 * Slides carry optional data-start / data-end Unix timestamps (seconds).
 * The server marks the first live slide active; this script re-checks the
 * schedule in the browser so cached pages stay accurate.
 */
import { reducedMotion } from '@theme/core';

class AnnouncementBar extends HTMLElement {
  #index = 0;
  #timer = null;
  #paused = false;
  #hoverPaused = false;

  connectedCallback() {
    this.slides = [...this.querySelectorAll('.announcement__slide')];
    this.live = this.slides.filter((slide) => this.#isLive(slide));

    if (this.live.length === 0) {
      this.setAttribute('data-empty', '');
      return;
    }

    // Hide navigation when scheduling leaves a single live slide
    this.querySelectorAll('[data-announcement-prev], [data-announcement-next], [data-announcement-pause]').forEach(
      (control) => (control.hidden = this.live.length < 2),
    );

    this.#show(Math.max(0, this.live.findIndex((slide) => slide.hasAttribute('data-active'))));

    this.querySelector('[data-announcement-prev]')?.addEventListener('click', () => this.#step(-1));
    this.querySelector('[data-announcement-next]')?.addEventListener('click', () => this.#step(1));
    this.pauseButton = this.querySelector('[data-announcement-pause]');
    this.pauseButton?.addEventListener('click', () => {
      this.#paused = !this.#paused;
      this.pauseButton.setAttribute('aria-pressed', String(this.#paused));
      this.#schedule();
    });

    this.addEventListener('pointerenter', () => this.#hold(true));
    this.addEventListener('pointerleave', () => this.#hold(false));
    this.addEventListener('focusin', () => this.#hold(true));
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.#hold(false);
    });

    // Theme editor: show the selected announcement and stop rotating
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.live.indexOf(event.target);
      if (index > -1) this.#show(index);
      this.#hold(true);
    });
    this.addEventListener('shopify:block:deselect', () => this.#hold(false));

    if (this.hasAttribute('data-autoplay') && reducedMotion()) {
      this.#paused = true;
      this.pauseButton?.setAttribute('aria-pressed', 'true');
    }
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
  }

  #isLive(slide) {
    const now = Date.now() / 1000;
    const start = Number(slide.dataset.start || 0);
    const end = Number(slide.dataset.end || 0);
    return (!start || start <= now) && (!end || end >= now);
  }

  #show(index) {
    this.#index = (index + this.live.length) % this.live.length;
    this.slides.forEach((slide) => slide.toggleAttribute('data-active', slide === this.live[this.#index]));
  }

  #step(direction) {
    this.#show(this.#index + direction);
    this.#schedule();
  }

  #hold(state) {
    this.#hoverPaused = state;
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (!this.hasAttribute('data-autoplay') || this.#paused || this.#hoverPaused || this.live.length < 2) return;
    this.#timer = setTimeout(() => this.#step(1), Number(this.dataset.interval) || 5000);
  }
}

if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
